/**
 * Call onboarding: calendario slot (stile Calendly) + salta piccolo.
 * Continua allo step Fine solo dopo prenotazione o skip.
 */
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import {
  customerStepPath,
  firstIncompleteCustomerStep,
  isCustomerStepReachable,
} from "@/components/onboarding/onboardingSteps";
import {
  bookOnboardingCall,
  fetchOnboardingByToken,
  listOnboardingCallSlots,
  skipOnboardingCall,
} from "@/lib/onboarding-public";
import { cn } from "@/lib/utils";

function formatDayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    timeZone: "Europe/Rome",
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function formatTimeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString("it-IT", {
    timeZone: "Europe/Rome",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function dayKey(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Europe/Rome" });
}

export default function OnboardingCallPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["onboarding-public", token],
    enabled: !!token,
    queryFn: () => fetchOnboardingByToken(token!),
    retry: false,
  });

  const slotsQuery = useQuery({
    queryKey: ["onboarding-call-slots", token],
    enabled: !!token && !!data,
    queryFn: () => listOnboardingCallSlots(token!),
    staleTime: 60_000,
  });

  const slotsByDay = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const iso of slotsQuery.data?.slots || []) {
      const k = dayKey(iso);
      const list = map.get(k) || [];
      list.push(iso);
      map.set(k, list);
    }
    return map;
  }, [slotsQuery.data?.slots]);

  const dayKeys = useMemo(() => [...slotsByDay.keys()], [slotsByDay]);
  const activeDay = selectedDay && slotsByDay.has(selectedDay) ? selectedDay : dayKeys[0] || null;
  const daySlots = activeDay ? slotsByDay.get(activeDay) || [] : [];

  // Blocca skip: pagamento incompleto → step dovuto
  useEffect(() => {
    if (!token || !data) return;
    if (!isCustomerStepReachable("call", data)) {
      const fallback = firstIncompleteCustomerStep(data);
      navigate(customerStepPath(token, fallback), { replace: true });
    }
  }, [token, data, navigate]);

  if (!token) return null;

  if (isLoading || error || !data) {
    return (
      <OnboardingShell
        token={token}
        step="call"
        loading={isLoading}
        error={error instanceof Error ? error.message : error ? "Errore" : null}
        hideContinue
      >
        {null}
      </OnboardingShell>
    );
  }

  const { onboarding } = data;
  const canAct =
    !!onboarding.access_ready ||
    (!!onboarding.tenant_is_trial && !!onboarding.payment_succeeded_at);
  const callDone =
    onboarding.call_status === "booked" || onboarding.call_status === "skipped";

  const onBook = async () => {
    if (!token || !selectedSlot) {
      toast.error("Seleziona data e orario");
      return;
    }
    setBusy(true);
    try {
      await bookOnboardingCall(token, selectedSlot);
      toast.success("Call prenotata");
      await refetch();
      navigate(`/onboarding/${token}/completato`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    } finally {
      setBusy(false);
    }
  };

  const onSkip = async () => {
    if (!token) return;
    setBusy(true);
    try {
      await skipOnboardingCall(token);
      toast.message("Call saltata — potrai prenotare dopo");
      await refetch();
      navigate(`/onboarding/${token}/completato`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <OnboardingShell
      token={token}
      step="call"
      tenantName={onboarding.tenant_name}
      payload={data}
      continueLabel="Continua"
      continueDisabled={!callDone}
      onContinue={() => {
        if (!callDone) return;
        navigate(`/onboarding/${token}/completato`);
      }}
    >
      <p className="text-sm text-muted-foreground leading-relaxed">
        {callDone
          ? onboarding.call_status === "booked"
            ? `Call prenotata${
                onboarding.call_scheduled_at
                  ? ` per ${formatDayLabel(onboarding.call_scheduled_at)} alle ${formatTimeLabel(onboarding.call_scheduled_at)}`
                  : ""
              }. Puoi continuare allo step Fine.`
            : "Hai saltato la call. Puoi continuare allo step Fine."
          : canAct
            ? "Scegli giorno e orario per la call di onboarding (30 minuti, fuso Italia)."
            : "Completa il pagamento canone per abilitare la prenotazione."}
      </p>

      <section className="rounded-2xl border border-border/70 bg-card p-5 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <CalendarDays className="h-4 w-4" />
          Prenota call di onboarding
        </div>

        {callDone && onboarding.call_status === "booked" ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-sm text-emerald-950">
            Prenotazione confermata
            {onboarding.call_scheduled_at
              ? `: ${formatDayLabel(onboarding.call_scheduled_at)} · ${formatTimeLabel(onboarding.call_scheduled_at)}`
              : ""}
          </div>
        ) : null}

        {!callDone && canAct ? (
          <>
            {slotsQuery.isLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : dayKeys.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nessuno slot libero nelle prossime due settimane. Usa “Salta per ora” oppure riprova
                più tardi.
              </p>
            ) : (
              <>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                  {dayKeys.map((k) => {
                    const sample = slotsByDay.get(k)?.[0];
                    const active = k === activeDay;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => {
                          setSelectedDay(k);
                          setSelectedSlot(null);
                        }}
                        className={cn(
                          "shrink-0 min-w-[4.75rem] rounded-xl border px-3 py-2.5 text-center transition-colors",
                          active
                            ? "border-emerald-600/40 bg-emerald-50 text-emerald-950"
                            : "border-border/70 bg-white text-muted-foreground hover:text-foreground",
                        )}
                      >
                        <p className="text-[11px] font-medium capitalize">
                          {sample ? formatDayLabel(sample) : k}
                        </p>
                      </button>
                    );
                  })}
                </div>

                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {daySlots.map((iso) => {
                    const active = selectedSlot === iso;
                    return (
                      <button
                        key={iso}
                        type="button"
                        onClick={() => setSelectedSlot(iso)}
                        className={cn(
                          "rounded-xl border h-11 text-sm font-medium tabular-nums transition-colors",
                          active
                            ? "border-emerald-600 bg-emerald-600 text-white"
                            : "border-border/70 bg-white hover:border-emerald-600/40",
                        )}
                      >
                        {formatTimeLabel(iso)}
                      </button>
                    );
                  })}
                </div>

                <Button
                  type="button"
                  className="w-full rounded-xl h-11"
                  disabled={busy || !selectedSlot}
                  onClick={() => void onBook()}
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Conferma prenotazione
                </Button>
              </>
            )}
          </>
        ) : null}

        {!callDone && canAct ? (
          <div className="pt-1 text-center">
            <button
              type="button"
              disabled={busy}
              onClick={() => void onSkip()}
              className="text-[11px] text-muted-foreground underline-offset-2 hover:underline hover:text-foreground disabled:opacity-50"
            >
              Salta per ora
            </button>
          </div>
        ) : null}

        {!canAct && !callDone ? (
          <p className="text-sm text-amber-900">
            Prenotazione disabilitata finché il canone con carta non risulta pagato.
          </p>
        ) : null}
      </section>

      {(!!onboarding.tenant_is_trial || !!onboarding.access_ready) && (
        <Button asChild variant="ghost" className="w-full rounded-xl">
          <Link to="/construction/login">Accedi alla piattaforma</Link>
        </Button>
      )}
    </OnboardingShell>
  );
}
